<template>
  <template v-if="variant === 'mock'">
    <div v-if="!user" id="authLinks" class="mock-auth">
      <a href="/signin">{{ mockLabels.signIn }}</a>
      <a href="/signup" class="mock-signup">{{ mockLabels.signUp }}</a>
    </div>

    <div v-else id="userDropdown" class="user-dropdown" v-click-outside="closeDropdown">
      <button
        id="dropdownTrigger"
        class="dropdown-trigger"
        type="button"
        :aria-expanded="String(isDropdownOpen)"
        aria-controls="dropdownMenu"
        @click="toggleDropdown"
      >
        <span id="userName">{{ displayName }}</span>
        <span class="dropdown-arrow" :class="{ 'arrow-up': isDropdownOpen }" aria-hidden="true"></span>
      </button>
      <div
        v-if="isDropdownOpen"
        id="dropdownMenu"
        class="dropdown-menu"
        :class="{ show: isDropdownOpen }"
      >
        <a href="/my-cards" class="dropdown-item" @click="closeDropdown">{{ mockLabels.myCards }}</a>
        <a href="/account" class="dropdown-item" @click="closeDropdown">{{ mockLabels.account }}</a>
        <a v-if="user.is_admin" href="/admin" class="dropdown-item" @click="closeDropdown">{{ mockLabels.admin }}</a>
        <a href="/logout" class="dropdown-item" @click="closeDropdown">{{ mockLabels.logout }}</a>
      </div>
    </div>

    <button
      class="mock-mobile-menu-toggle"
      type="button"
      id="mobileMenuToggle"
      :aria-expanded="String(isMobileMenuOpen)"
      aria-controls="mobileMenu"
      aria-haspopup="dialog"
      :aria-label="mockLabels.menuOpen"
      @click.stop.prevent="toggleMobileMenu"
    >
      <span aria-hidden="true">…</span>
    </button>

    <Teleport to="body">
      <div
        id="mobileMenu"
        class="mock-mobile-menu"
        :data-open="String(isMobileMenuOpen)"
        :aria-hidden="String(!isMobileMenuOpen)"
        role="dialog"
        aria-modal="true"
      >
        <div class="mock-mobile-menu-head">
          <a :href="mobileHomePath" class="mock-mobile-menu-logo" :aria-label="mockLabels.homeLabel">
            <img src="/images/logo/logo.svg" alt="Sharewell" width="154" height="57">
          </a>
          <button
            class="mock-mobile-menu-close"
            type="button"
            id="mobileMenuClose"
            :aria-label="mockLabels.menuClose"
            @click="closeMobileMenu(true)"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <nav class="mock-mobile-menu-nav" :aria-label="mockLabels.navLabel">
          <a
            v-for="link in mobileLinks"
            :key="link.href"
            :href="link.href"
            @click="closeMobileMenu()"
          >{{ link.label }}</a>
          <div class="mock-mobile-menu-divider" aria-hidden="true"></div>
          <div v-if="!user" id="mobileAuthLinks" class="mock-mobile-auth">
            <a href="/signin" @click="closeMobileMenu()">{{ mockLabels.signIn }}</a>
            <a href="/signup" @click="closeMobileMenu()">{{ mockLabels.signUp }}</a>
          </div>
          <div
            v-else
            id="mobileUserLinks"
            class="mock-mobile-user"
            style="display: grid;"
          >
            <a href="/my-cards" @click="closeMobileMenu()">{{ mockLabels.myCards }}</a>
            <a href="/account" @click="closeMobileMenu()">{{ mockLabels.account }}</a>
            <a v-if="user.is_admin" href="/admin" id="mobileAdminMenuItem" @click="closeMobileMenu()">{{ mockLabels.admin }}</a>
            <a href="/logout" @click="closeMobileMenu()">{{ mockLabels.logout }}</a>
          </div>
        </nav>
      </div>
    </Teleport>
  </template>

  <div v-else class="user-menu" :class="[customClass, `theme-${theme}`]">
    <!-- Logged out state -->
    <div v-if="!user" class="auth-links">
      <router-link to="/signin" class="auth-link">
        <span class="auth-text">{{ $t('auth.signin') }}</span>
      </router-link>
      <router-link to="/signup" class="auth-link signup-auth-link" v-if="!compact">
        <span class="auth-text">{{ $t('auth.signup') }}</span>
      </router-link>
    </div>

    <!-- Logged in state -->
    <div v-else class="user-dropdown" v-click-outside="closeDropdown">
      <button
        class="dropdown-trigger"
        type="button"
        :aria-label="$t('auth.account')"
        :aria-expanded="String(isDropdownOpen)"
        aria-haspopup="menu"
        @click="toggleDropdown"
      >
        <span class="user-email">{{ user.email }}</span>
        <span class="arrow" :class="{ 'arrow-up': isDropdownOpen }" aria-hidden="true"></span>
      </button>
      <div v-if="isDropdownOpen" class="dropdown-menu">
        <router-link to="/my-cards" class="dropdown-item" @click="closeDropdown">
          <span class="item-text">{{ $t('auth.myCards') }}</span>
        </router-link>
        <router-link to="/account" class="dropdown-item" @click="closeDropdown">
          <span class="item-text">{{ $t('auth.account') }}</span>
        </router-link>
        <router-link v-if="user.is_admin" to="/admin" class="dropdown-item" @click="closeDropdown">
          <span class="item-text">{{ $t('auth.admin') }}</span>
        </router-link>
        <router-link to="/logout" class="dropdown-item" @click="closeDropdown">
          <span class="item-text">{{ $t('auth.logout') }}</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import eventBus from './eventBus';
import { computed, ref, toRef, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const MOCK_COPY = {
  en: {
    signIn: 'Sign in',
    signUp: 'Sign up',
    accountFallback: 'Account',
    myCards: 'My cards',
    account: 'Account settings',
    admin: 'Admin',
    logout: 'Log out',
    homeLabel: 'Sharewell home',
    navLabel: 'Primary navigation',
    menuOpen: 'Open menu',
    menuClose: 'Close menu'
  },
  fr: {
    signIn: 'Connexion',
    signUp: 'Inscription',
    accountFallback: 'Compte',
    myCards: 'Mes cartes',
    account: 'Paramètres du compte',
    admin: 'Administration',
    logout: 'Déconnexion',
    homeLabel: 'Accueil Sharewell',
    navLabel: 'Navigation principale',
    menuOpen: 'Ouvrir le menu',
    menuClose: 'Fermer le menu'
  },
  es: {
    signIn: 'Iniciar sesión',
    signUp: 'Registrarse',
    accountFallback: 'Cuenta',
    myCards: 'Mis tarjetas',
    account: 'Configuración de la cuenta',
    admin: 'Administración',
    logout: 'Cerrar sesión',
    homeLabel: 'Inicio de Sharewell',
    navLabel: 'Navegación principal',
    menuOpen: 'Abrir el menú',
    menuClose: 'Cerrar el menú'
  }
};

export default {
  name: 'UserMenu',
  props: {
    customClass: {
      type: String,
      default: ''
    },
    theme: {
      type: String,
      default: 'default' // can be 'default', 'light', 'dark', etc.
    },
    compact: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default'
    },
    mobileLinks: {
      type: Array,
      default: () => []
    },
    mobileHomePath: {
      type: String,
      default: '/'
    }
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  },
  setup(props) {
    const { locale } = useI18n();
    const user = ref(null);
    const isDropdownOpen = ref(false);
    const isMobileMenuOpen = ref(false);
    const mobileLinks = toRef(props, 'mobileLinks');
    const mobileHomePath = toRef(props, 'mobileHomePath');
    const mockLabels = computed(() => MOCK_COPY[locale.value] || MOCK_COPY.en);
    const displayName = computed(() => {
      if (!user.value) return mockLabels.value.accountFallback;
      return user.value.name || user.value.email || mockLabels.value.accountFallback;
    });

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const setMobileMenuOpen = (isOpen, restoreFocus = false) => {
      isMobileMenuOpen.value = isOpen;
      document.documentElement.classList.toggle('mobile-menu-open', isOpen);
      if (!isOpen && restoreFocus) {
        document.getElementById('mobileMenuToggle')?.focus();
      }
    };

    const closeMobileMenu = (restoreFocus = false) => {
      setMobileMenuOpen(false, restoreFocus);
    };

    const toggleMobileMenu = () => {
      // The desktop account control can still be open when the viewport crosses
      // the mobile breakpoint. Close it before exposing the mobile panel so the
      // two interaction layers can never compete for the same click.
      closeDropdown();
      setMobileMenuOpen(!isMobileMenuOpen.value);
    };

    const handleMobileMenuKeydown = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen.value) {
        closeMobileMenu(true);
      }
    };

    const fetchUser = async () => {
      try {
        const response = await axios.get('/auth/me');
        user.value = response.data.user;
      } catch {
        user.value = null;
      }
    };

    const handleLogin = async (callback) => {
      await fetchUser();
      if (callback) callback();
    };

    const handleLogout = (callback) => {
      user.value = null;
      closeDropdown();
      closeMobileMenu();
      if (callback) callback();
    };

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    onMounted(() => {
      fetchUser();
      eventBus.on('auth:login', handleLogin);
      eventBus.on('auth:logout', handleLogout);
      document.addEventListener('keydown', handleMobileMenuKeydown);
    });

    onBeforeUnmount(() => {
      eventBus.off('auth:login', handleLogin);
      eventBus.off('auth:logout', handleLogout);
      document.removeEventListener('keydown', handleMobileMenuKeydown);
      document.documentElement.classList.remove('mobile-menu-open');
    });

    return {
      user,
      displayName,
      isDropdownOpen,
      isMobileMenuOpen,
      mockLabels,
      mobileLinks,
      mobileHomePath,
      fetchUser,
      handleLogin,
      handleLogout,
      toggleDropdown,
      closeDropdown,
      toggleMobileMenu,
      closeMobileMenu
    };
  }
};
</script>

<style scoped>
.user-menu {
  --ink: #302a27;
  --ink-soft: #6c6159;
  --paper-cream: #fffdf7;
  --paper-line: rgba(87, 65, 52, 0.16);
  --title-red: #c94750;
  --coral: #e75a62;
  --coral-hover: #d64b55;
  --radius-md: 14px;
  position: relative;
  display: inline-block;
}

/* Theme variations */
.user-menu.theme-light .auth-link,
.user-menu.theme-light .dropdown-trigger {
  color: white;
}

.user-menu.theme-dark .auth-link,
.user-menu.theme-dark .dropdown-trigger {
  color: #333;
}

.auth-links {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.mock-auth {
  --ink: #302a27;
  --ink-soft: #6c6159;
  --paper-cream: #fffdf7;
  --paper-line: rgba(87, 65, 52, 0.16);
  --title-red: #c94750;
  --coral: #e75a62;
  --coral-hover: #d64b55;
  --radius-md: 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.user-dropdown {
  --ink: #302a27;
  --ink-soft: #6c6159;
  --paper-cream: #fffdf7;
  --paper-line: rgba(87, 65, 52, 0.16);
  --title-red: #c94750;
  --radius-md: 14px;
  position: relative;
}

.mock-auth a {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  color: inherit;
  font-weight: 800;
  padding: 0 16px;
  text-decoration: none;
  transition:
    color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    border-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    background-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

.mock-auth a:hover {
  color: var(--ink);
}

.mock-signup {
  border: 1px solid var(--coral);
  background: var(--coral);
  color: var(--paper-cream) !important;
}

.mock-signup:hover {
  border-color: var(--coral-hover);
  background: var(--coral-hover);
}

.auth-link {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  color: var(--ink);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 800;
  padding: 0 14px;
  white-space: nowrap;
  transition:
    color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 220ms cubic-bezier(0.16, 1, 0.3, 1),
    border-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    background-color 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

.auth-link:hover {
  color: var(--title-red);
  transform: translateY(-1px);
}

.signup-auth-link {
  border: 1px solid var(--coral);
  background: var(--coral);
  color: var(--paper-cream) !important;
}

.signup-auth-link:hover {
  border-color: var(--coral-hover);
  background: var(--coral-hover);
  color: var(--paper-cream) !important;
}

.dropdown-trigger {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--paper-line);
  border-radius: var(--radius-md);
  background: rgba(255, 253, 247, 0.82);
  color: var(--ink);
  cursor: pointer;
  padding: 0 14px;
  font: inherit;
  font-size: 0.92rem;
  transition:
    background-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    color 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

.dropdown-trigger:hover {
  background: var(--paper-cream);
  color: var(--title-red);
}

.arrow,
.dropdown-arrow {
  width: 0;
  height: 0;
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
  border-top: 7px solid currentColor;
  flex: 0 0 auto;
  transition: transform 180ms ease;
}

.arrow-up {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  display: grid;
  min-width: 210px;
  padding: 8px;
  border: 1px solid var(--paper-line);
  border-radius: var(--radius-md);
  background: var(--paper-cream);
  box-shadow: 0 18px 44px -28px rgba(63, 44, 34, 0.5);
  z-index: 1000;
}

.dropdown-menu.show {
  display: grid;
}

.dropdown-item {
  display: flex;
  align-items: center;
  border-radius: 10px;
  color: var(--ink-soft);
  padding: 10px 12px;
  text-decoration: none;
  transition:
    background-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    color 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

.item-text {
  flex: 1;
}

.dropdown-item:hover {
  background: rgba(231, 90, 98, 0.08);
  color: var(--title-red);
}

@media (max-width: 520px) {
  .auth-links {
    gap: 8px;
  }

  .auth-link,
  .dropdown-trigger {
    min-height: 38px;
    padding: 0 12px;
  }

  .mock-auth {
    justify-content: start;
  }

  .mock-auth a {
    min-height: 38px;
    padding: 0 12px;
  }
}
</style>
